import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api'

/** Права на один раздел. */
export interface SectionAccess {
  view?: boolean
  edit?: boolean
}

/** Структура roles.permissions. */
export interface RolePermissions {
  all?: boolean
  sections?: Record<string, SectionAccess>
}

export interface Role {
  id: string
  tenant_id: string
  name: string
  permissions: RolePermissions
  created_at: string
}

/** Один раздел из реестра приложения. */
export interface SectionDef {
  key: string
  label: string
  medical: boolean
}

/** Ответ GET /roles/me/permissions. */
export interface MyPermissions {
  role_id: string | null
  role_name: string | null
  permissions: RolePermissions
  is_medical_organization: boolean
  sections: SectionDef[]
}

export interface RoleFilters {
  search?: string
  page?: number
  limit?: number
  sort?: string
}

export interface PaginatedRoles {
  data: Role[]
  total: number
  page: number
  limit: number
  totalPages: number
}

const ROLES_QUERY_KEY = 'roles'
const SECTIONS_QUERY_KEY = 'role-sections'
const MY_PERMISSIONS_QUERY_KEY = 'my-permissions'

export const useRoles = (filters: RoleFilters = {}) => {
  return useQuery({
    queryKey: [ROLES_QUERY_KEY, filters],
    queryFn: async (): Promise<PaginatedRoles> => {
      const response = await apiClient.getRoles(filters)
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 100,
        totalPages: response.totalPages || 1,
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
}

export const useRole = (id: string) => {
  return useQuery({
    queryKey: [ROLES_QUERY_KEY, id],
    queryFn: async (): Promise<Role> => apiClient.getRole(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

/** Реестр разделов приложения (для редактора ролей). */
export const useSections = () => {
  return useQuery({
    queryKey: [SECTIONS_QUERY_KEY],
    queryFn: async (): Promise<SectionDef[]> => apiClient.getRoleSections(),
    staleTime: 30 * 60 * 1000,
    retry: 1,
  })
}

/** Права текущего пользователя (его роль) + реестр разделов. */
export const useMyPermissions = () => {
  return useQuery({
    queryKey: [MY_PERMISSIONS_QUERY_KEY],
    queryFn: async (): Promise<MyPermissions> => apiClient.getMyPermissions(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
}

export const useCreateRole = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; permissions?: RolePermissions }) =>
      apiClient.createRole(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] })
    },
  })
}

export const useUpdateRole = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: { name?: string; permissions?: RolePermissions }
    }) => apiClient.updateRole(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] })
      void qc.invalidateQueries({ queryKey: [MY_PERMISSIONS_QUERY_KEY] })
    },
  })
}

export const useDeleteRole = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteRole(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] })
    },
  })
}

/** Проверка доступа к разделу по правам роли. `edit` подразумевает `view`. */
export function hasSectionAccess(
  permissions: RolePermissions | null | undefined,
  section: string,
  level: 'view' | 'edit' = 'view',
): boolean {
  if (!permissions) return false
  if (permissions.all === true) return true
  const entry = permissions.sections?.[section]
  if (!entry) return false
  if (level === 'view') return entry.view === true || entry.edit === true
  return entry.edit === true
}
